import { format as dfFormat } from "date-fns";
import { localizeDateNames } from "@/lib/dateNames";
const _WD_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
const FI_CAL_FORMATTERS = {
    formatCaption: (d) => localizeDateNames(dfFormat(d, "LLLL y"), { longMonth: true }),
    formatWeekdayName: (d) => i18n.t(`weekdaysShort.${_WD_KEYS[d.getDay()]}`),
};
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatDateShort, formatDateTime } from "@/lib/formatDate";
import { useApp } from "@/context/AppContext";
import { useRestaurantScope } from "@/hooks/useRestaurantScope";
import { useResolvedConfig } from "@/context/ResolvedConfigContext";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import {
    ThermometerSun, Snowflake, Truck, Timer, Sparkles, Plus, Upload, FileText,
    CalendarOff, CalendarX, Flame, AlertTriangle, Calendar as CalendarIcon,
} from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { apiErrorMessage } from "@/lib/apiErrorMessage";

// Calendar explanations arrive as fixed English server text; show them in the interface language.
function reasonText(x, t) {
    let m = x.match(/^Missing (fridge|freezer|hot holding) reading: (.*)$/);
    if (m) {
        const kind = t(`dailyChecks.reasonKind.${m[1].replace(" ", "_")}`, m[1]);
        return t("dailyChecks.reasonMissingReading", { kind, name: m[2], defaultValue: x });
    }
    if (x === "Missing deep cleaning answer") return t("dailyChecks.reasonMissingDeep", x);
    m = x.match(/^Abnormal reading without corrective action: (.*) (-?[\d.]+) °C$/);
    if (m) {
        const temp = i18n.language === "fi" ? m[2].replace(".", ",") : m[2];
        return t("dailyChecks.reasonAbnormal", { name: m[1], temp, defaultValue: x });
    }
    return x;
}


// Prompt 48 · Selected operational date shared with every child form
// (FridgeForm, IncomingForm, CoolingForm, SurfaceForm). Defaults to
// today. Never future. Consumed via useSelectedDate().
const SelectedDateCtx = createContext(new Date().toISOString().slice(0, 10));
const useSelectedDate = () => useContext(SelectedDateCtx);


const parseIsoDate = (iso, fallbackIso) => {
    const [y, m, d] = (iso || fallbackIso).split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
};

function SelfMonitoringDatePopover({ selectedDate, setSelectedDate, todayIso,
                                      restaurantId, scopedParams, refreshTick }) {
    // Compact popover trigger around a Shadcn Calendar. Fetches
    // missed-days for the DISPLAYED MONTH only (single scoped request),
    // paints those dates red via the DayPicker `modifiers` prop.
    const { t } = useTranslation();
    const parse = (iso) => parseIsoDate(iso, todayIso);
    const [open, setOpen] = useState(false);
    const [month, setMonth] = useState(() => parse(selectedDate));
    const [missed, setMissed] = useState([]);
    const [status, setStatus] = useState("evaluable");
    const [evalStart, setEvalStart] = useState(null);
    const [complete, setComplete] = useState([]);
    const [reasons, setReasons] = useState({});
    const monthKey = `${month.getFullYear()}-${String(month.getMonth()+1).padStart(2,"0")}`;

    useEffect(() => {
        if (!restaurantId) return;
        const [y, mo] = monthKey.split("-").map(Number);
        const first = new Date(y, mo - 1, 1);
        const last  = new Date(y, mo, 0);
        const iso = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
        let cancelled = false;
        api.get("/daily-checks/missed-days",
            { params: { ...scopedParams, from: iso(first), to: iso(last) } })
          .then(({ data }) => {
              if (cancelled) return;
              setMissed(data?.missed_dates || []);
              setComplete(data?.complete_dates || []);
              setReasons(data?.reasons || {});
              setStatus(data?.status || "evaluable");
              setEvalStart(data?.evaluation_starts_on || null);
          })
          .catch(() => {
              if (cancelled) return;
              setMissed([]); setComplete([]); setReasons({}); setStatus("evaluable"); setEvalStart(null);
          });
        return () => { cancelled = true; };
    }, [restaurantId, monthKey, refreshTick, scopedParams]);

    const missedDates = useMemo(
        () => missed.map((iso) => parseIsoDate(iso, todayIso)), [missed, todayIso]);
    const completeDates = useMemo(
        () => complete.map((iso) => parseIsoDate(iso, todayIso)), [complete, todayIso]);
    const selReasons = reasons[selectedDate] || [];
    const today = parse(todayIso);
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button data-testid="daily-checks-date-picker"
                        className="h-10 px-3 inline-flex items-center gap-2 rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50">
                    <CalendarIcon className="h-4 w-4" />
                    {selectedDate}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0" data-testid="daily-checks-calendar-popover">
                <Calendar
                    mode="single"
                    formatters={i18n.language === "fi" ? FI_CAL_FORMATTERS : undefined}
                    selected={parse(selectedDate)}
                    month={month}
                    onMonthChange={setMonth}
                    disabled={{ after: today }}
                    modifiers={{ missed: missedDates, complete: completeDates }}
                    modifiersClassNames={{
                        missed: "bg-red-100 text-red-900 font-semibold ring-1 ring-red-300",
                        complete: "bg-emerald-100 text-emerald-900 font-semibold ring-1 ring-emerald-300",
                    }}
                    onSelect={(d) => {
                        if (!d) return;
                        const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                        if (iso > todayIso) return;
                        setSelectedDate(iso);
                        setOpen(false);
                    }}
                    initialFocus
                />
                {missed.includes(selectedDate) && selReasons.length > 0 && (
                    <div data-testid="daily-checks-day-reasons"
                        className="border-t border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 max-w-[280px]">
                        <div className="font-semibold mb-1">{selectedDate}</div>
                        <ul className="list-disc pl-4 space-y-0.5">
                            {selReasons.map((x) => <li key={x}>{reasonText(x, t)}</li>)}
                        </ul>
                    </div>
                )}
                {complete.includes(selectedDate) && (
                    <div data-testid="daily-checks-day-complete"
                        className="border-t border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                        {t("dailyChecks.dayComplete", "All scheduled controls complete.")}
                    </div>
                )}
                {status === "not_evaluable" && (
                    <div data-testid="daily-checks-history-unavailable"
                        className="border-t border-neutral-200 px-3 py-2 text-xs text-neutral-600">
                        {t("dailyChecks.historyUnavailable", "Historical schedule unavailable.")}
                    </div>
                )}
                {status === "evaluable" && evalStart && (
                    <div data-testid="daily-checks-evaluation-start"
                        className="border-t border-neutral-200 px-3 py-2 text-xs text-neutral-500">
                        {t("dailyChecks.evaluationStarts", "Evaluation starts")} <span className="font-semibold text-neutral-700">{evalStart}</span>.
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}

const STATUS_TONE = {
    completed: "completed", in_progress: "pending", pending: "pending",
    action_required: "action_required",
};
const STATUS_LABEL = {
    completed: () => i18n.t("dailyChecks.status.completed", "Completed"), in_progress: () => i18n.t("dailyChecks.status.in_progress", "In Progress"),
    pending: () => i18n.t("dailyChecks.status.pending", "Pending"), action_required: () => i18n.t("dailyChecks.status.action_required", "Action Required"),
};

export default function DailyChecks() {
    const { user, restaurantId } = useApp();
    const { isFeatureEnabled } = useResolvedConfig();
    const hotHoldingEnabled = isFeatureEnabled("hot_holding_checks");
    const { scopedParams: params } = useRestaurantScope();
    const { t } = useTranslation();

    const [today, setToday] = useState(null);
    const [openSection, setOpenSection] = useState(null);
    const [historySection, setHistorySection] = useState(null);
    const [equipmentOpen, setEquipmentOpen] = useState(false);
    const [skipOpen, setSkipOpen] = useState(false);
    const [complianceOpen, setComplianceOpen] = useState(false);
    // Prompt 48 · selected operational date. Defaults to today; chef
    // may switch to any past date to record forgotten checks. Future
    // rejected client-side + server-side.
    const todayIso = new Date().toISOString().slice(0, 10);
    const [selectedDate, setSelectedDate] = useState(todayIso);

    const load = useCallback(async () => {
        if (!restaurantId) return;
        const { data } = await api.get("/daily-checks/today",
            { params: { ...params, date: selectedDate } });
        setToday({ ...data, _tick: Date.now() });
    }, [restaurantId, selectedDate, params]);
    useEffect(() => { load(); }, [load]);

    if (!today) {
        return <div className="px-6 lg:px-12 py-10 text-sm text-neutral-500">{t("common.loading", "Loading…")}</div>;
    }

    const isScheduledToday = (today.is_scheduled_on_selected ?? today.is_scheduled_today) !== false;
    const skippedToday = today.skipped_today || null;
    const activeToday = isScheduledToday && !skippedToday;

    const sections = [
        { key: "fridge", title: t("dailyChecks.section.fridge", "Fridge Temperatures"), icon: ThermometerSun,
            data: today.sections.fridge,
            description: today.sections.fridge.required.length
                ? t("dailyChecks.fridgesLogged", { defaultValue: "{{logged}} of {{required}} fridges logged today", logged: today.sections.fridge.logs.length, required: today.sections.fridge.required.length })
                : t("dailyChecks.noFridges", "No fridges configured") },
        { key: "freezer", title: t("dailyChecks.section.freezer", "Freezer Temperatures"), icon: Snowflake,
            data: today.sections.freezer,
            description: today.sections.freezer.required.length
                ? t("dailyChecks.freezersLogged", { defaultValue: "{{logged}} of {{required}} freezers logged today", logged: today.sections.freezer.logs.length, required: today.sections.freezer.required.length })
                : t("dailyChecks.noFreezers", "No freezers configured") },
        // Launch Fix R6 — Hot Holding Temperature. Same UI + workflow as
        // fridge/freezer. Only shows real progress once equipment exists.
        // Config Phase 1: entire section hidden when the tenant has
        // `hot_holding_checks` disabled (feature-flag gated).
        ...(hotHoldingEnabled ? [{ key: "hot_holding", title: t("dailyChecks.section.hot_holding", "Hot Holding Temperature"), icon: Flame,
            data: today.sections.hot_holding || { status: "pending", logs: [], required: [] },
            description: (today.sections.hot_holding?.required?.length)
                ? t("dailyChecks.hotHoldersLogged", { defaultValue: "{{logged}} of {{required}} hot holders logged today", logged: today.sections.hot_holding.logs.length, required: today.sections.hot_holding.required.length })
                : t("dailyChecks.noHotHolders", "No hot holders configured") }] : []),
        { key: "incoming", title: t("dailyChecks.section.incoming", "Incoming Product Temperature"), icon: Truck,
            data: today.sections.incoming,
            description: t("dailyChecks.entriesToday", { defaultValue: "{{count}} entries today", count: today.sections.incoming.logs.length }) },
        { key: "cooling", title: t("dailyChecks.section.cooling", "Cooling Log"), icon: Timer,
            data: today.sections.cooling,
            description: t("dailyChecks.entriesToday", { defaultValue: "{{count}} entries today", count: today.sections.cooling.logs.length }) },
        { key: "deep_cleaning", title: t("dailyChecks.section.deep_cleaning", "Deep Cleaning"), icon: Sparkles,
            data: today.deep_cleaning || { status: "completed" },
            description: today.deep_cleaning?.answer
                ? t("dailyChecks.deepAnswered", { defaultValue: "Answered: {{a}}", a: today.deep_cleaning.answer === "yes" ? t("dailyChecks.answerYes", "Yes") : t("dailyChecks.answerNo", "No") })
                : t("dailyChecks.deepNotAnswered", "Was deep cleaning carried out? Not answered yet") },
        { key: "surface", title: t("dailyChecks.section.surface", "Surface Hygiene Tests"), icon: Sparkles,
            data: today.sections.surface,
            description: today.sections.surface.overdue?.length
                ? t("dailyChecks.overdueCount", { defaultValue: "{{count}} overdue", count: today.sections.surface.overdue.length })
                : t("dailyChecks.scheduledCount", { defaultValue: "{{count}} scheduled", count: today.sections.surface.upcoming?.length || 0 }) },
    ];

    return (
        <SelectedDateCtx.Provider value={selectedDate}>
        <div className="px-6 lg:px-12 py-10 max-w-6xl mx-auto">
            <PageHeader
                title={t("dailyChecks.title", "Self Monitoring")}
                subtitle={t("dailyChecks.subtitle", "Omavalvonta — have all required food safety checks been completed today?")}
                stackOnMobile
                action={
                    <div data-testid="daily-checks-header-actions" className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
                        <SelfMonitoringDatePopover
                            selectedDate={selectedDate}
                            setSelectedDate={setSelectedDate}
                            todayIso={todayIso}
                            restaurantId={restaurantId}
                            scopedParams={params}
                            refreshTick={today?._tick}
                        />
                        <button
                            data-testid="daily-checks-yesterday"
                            onClick={() => {
                                const d = new Date(`${todayIso}T00:00:00Z`);
                                d.setUTCDate(d.getUTCDate() - 1);
                                setSelectedDate(d.toISOString().slice(0, 10));
                            }}
                            className="h-10 px-3 inline-flex items-center gap-2 rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50"
                        >
                            {t("dailyChecks.yesterday", "Yesterday")}
                        </button>
                        <button
                            data-testid="skip-today-button"
                            onClick={() => setSkipOpen(true)}
                            disabled={!!skippedToday}
                            className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <CalendarOff className="h-4 w-4" /> {t("dailyChecks.skipDay", "Skip Day")}
                        </button>
                        <button
                            data-testid="manage-equipment-button"
                            onClick={() => setEquipmentOpen(true)}
                            className="h-10 px-4 inline-flex items-center gap-2 rounded-md border border-neutral-200 text-sm font-semibold hover:bg-neutral-50"
                        >
                            <Plus className="h-4 w-4" /> {t("dailyChecks.equipment", "Equipment")}
                        </button>
                        <button
                            data-testid="compliance-pdf-button"
                            onClick={() => setComplianceOpen(true)}
                            className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800"
                        >
                            <FileText className="h-4 w-4" /> {t("dailyChecks.compliancePdf", "Compliance PDF")}
                        </button>
                    </div>
                }
            />

            {!activeToday && (
                <InactiveBanner
                    isScheduledToday={isScheduledToday}
                    skipped={skippedToday}
                    isHistorical={selectedDate !== todayIso}
                />
            )}

            <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${activeToday ? "" : "opacity-60 pointer-events-none"}`}>
                {sections.map((s) => (
                    <div key={s.key} className="kop-card p-5" data-testid={`section-${s.key}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                                <div className="h-10 w-10 grid place-items-center rounded-md bg-neutral-100">
                                    <s.icon className="h-4 w-4 text-neutral-700" />
                                </div>
                                <div>
                                    <div className="font-display text-lg font-bold">{s.title}</div>
                                    <div className="text-xs text-neutral-500 mt-1">{s.description}</div>
                                </div>
                            </div>
                            <StatusBadge status={STATUS_TONE[s.data.status]}>
                                {STATUS_LABEL[s.data.status]?.()}
                            </StatusBadge>
                        </div>
                        <div className="mt-4 flex items-center gap-2">
                            <button data-testid={`record-${s.key}`} onClick={() => setOpenSection(s.key)}
                                className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-neutral-900 text-white text-xs font-semibold hover:bg-neutral-800">
                                <Plus className="h-3.5 w-3.5" /> {t("dailyChecks.record", "Record")}
                            </button>
                            <button data-testid={`history-${s.key}`} onClick={() => setHistorySection(s.key)}
                                className="h-9 px-3 rounded-md border border-neutral-200 text-xs font-semibold hover:bg-neutral-50">
                                {t("dailyChecks.viewHistory", "View history")}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <RecordDialog
                section={openSection} onClose={() => setOpenSection(null)}
                onSaved={() => { setOpenSection(null); load(); }}
                onRecorded={load}
                equipment={today.equipment}
                today={today}
            />
            <HistoryDialog section={historySection} onClose={() => setHistorySection(null)} />
            <EquipmentDialog open={equipmentOpen} onClose={() => setEquipmentOpen(false)}
                onSaved={() => load()} equipment={today.equipment} />
            <SkipTodayDialog open={skipOpen} onClose={() => setSkipOpen(false)}
                onSaved={() => { setSkipOpen(false); load(); }} />
            <ComplianceReportDialog open={complianceOpen}
                onClose={() => setComplianceOpen(false)} />
        </div>
        </SelectedDateCtx.Provider>
    );
}

const SKIP_REASONS = [
    { value: "closed", label: () => i18n.t("dailyChecks.skip.closed", "Restaurant closed") },
    { value: "holiday", label: () => i18n.t("dailyChecks.skip.holiday", "Public holiday") },
    { value: "vacation", label: () => i18n.t("dailyChecks.skip.vacation", "Vacation") },
    { value: "maintenance", label: () => i18n.t("dailyChecks.skip.maintenance", "Maintenance") },
    { value: "no_operation", label: () => i18n.t("dailyChecks.skip.no_operation", "No operation today") },
    { value: "other", label: () => i18n.t("dailyChecks.skip.other", "Other") },
];

function InactiveBanner({ isScheduledToday, skipped, isHistorical }) {
    const { t } = useTranslation();
    if (skipped) {
        const label = SKIP_REASONS.find((r) => r.value === skipped.reason)?.label() || t("dailyChecks.skipped", "Skipped");
        return (
            <div data-testid="daily-checks-skipped-banner"
                className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
                <CalendarX className="h-5 w-5 text-amber-700 mt-0.5" />
                <div className="text-sm">
                    <div className="font-semibold text-amber-900">
                        {isHistorical ? t("dailyChecks.skippedOnDate", "Daily Checks skipped on this date") : t("dailyChecks.skippedToday", "Daily Checks skipped today")}
                    </div>
                    <div className="text-amber-800">
                        {t("dailyChecks.reasonLabel", "Reason:")} {label}{skipped.notes ? ` — ${skipped.notes}` : ""}.{" "}
                        {isHistorical ? t("dailyChecks.notOverdueDate", "This date's checks won't be counted as overdue.") : t("dailyChecks.notOverdueToday", "Today's checks won't be counted as overdue.")}
                    </div>
                </div>
            </div>
        );
    }
    if (!isScheduledToday) {
        return (
            <div data-testid="daily-checks-inactive-banner"
                className="mb-4 rounded-md border border-neutral-200 bg-neutral-50 px-4 py-3 flex items-start gap-3">
                <CalendarOff className="h-5 w-5 text-neutral-500 mt-0.5" />
                <div className="text-sm">
                    <div className="font-semibold text-neutral-900">
                        {t("dailyChecks.notCheckDay", "Not a Daily Check day")}
                    </div>
                    <div className="text-neutral-600">
                        {isHistorical
                          ? t("dailyChecks.notScheduledDate", "This date is not scheduled as a Daily Check Day per the restaurant's schedule. Pick another date, or adjust the schedule in Settings → Operating Days & Daily Checks.")
                          : t("dailyChecks.notScheduledToday", "Daily Checks are not scheduled for today per the restaurant's Daily Check Days. You can adjust the schedule in Settings → Operating Days & Daily Checks.")}
                    </div>
                </div>
            </div>
        );
    }
    return null;
}

function SkipTodayDialog({ open, onClose, onSaved }) {
    const { withRestaurant } = useRestaurantScope();
    const selectedDate = useSelectedDate();
    const { t } = useTranslation();
    const [reason, setReason] = useState("closed");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true);
        try {
            const body = withRestaurant({ reason, notes,
                check_date: selectedDate });
            await api.post("/daily-checks/skip-today", body);
            toast.success(selectedDate === new Date().toISOString().slice(0, 10)
                ? t("dailyChecks.todaySkippedToast", "Today marked as skipped")
                : t("dailyChecks.dateSkippedToast", { defaultValue: "{{date}} marked as skipped", date: selectedDate }));
            onSaved();
        } catch (e) {
            toast.error(apiErrorMessage(e) || t("dailyChecks.couldNotSkip", "Could not skip day"));
        } finally { setSaving(false); }
    };
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent data-testid="skip-today-dialog">
                <DialogHeader>
                    <DialogTitle>{t("dailyChecks.skipDialogTitle", "Skip Daily Checks today")}</DialogTitle>
                    <DialogDescription>
                        {t("dailyChecks.skipDialogDescription", "For exceptional closures (holidays, vacations, maintenance). Today's checks won't reduce compliance.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.reason", "Reason")}</label>
                        <Select value={reason} onValueChange={setReason}>
                            <SelectTrigger data-testid="skip-today-reason" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {SKIP_REASONS.map((r) => (
                                    <SelectItem key={r.value} value={r.value}>{r.label()}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.notesOptional", "Notes (optional)")}</label>
                        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                            data-testid="skip-today-notes"
                            className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm" />
                    </div>
                </div>
                <DialogFooter>
                    <button onClick={onClose}
                        className="h-10 px-4 rounded-md border border-neutral-300 text-sm hover:bg-neutral-50">
                        {t("common.cancel", "Cancel")}
                    </button>
                    <button data-testid="skip-today-confirm" onClick={save} disabled={saving}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                        {saving ? t("common.saving", "Saving…") : t("dailyChecks.skipToday", "Skip today")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function RecordDialog({ section, onClose, onSaved, onRecorded, equipment, today }) {
    if (!section) return null;
    const logs = (k) => today?.sections?.[k]?.logs || [];
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent data-testid={`record-dialog-${section}`}>
                {section === "fridge" && <TemperatureForm type="fridge" equipment={equipment.fridges} logs={logs("fridge")} onRecorded={onRecorded} onSaved={onSaved} />}
                {section === "freezer" && <TemperatureForm type="freezer" equipment={equipment.freezers} logs={logs("freezer")} onRecorded={onRecorded} onSaved={onSaved} />}
                {section === "hot_holding" && <TemperatureForm type="hot_holding" equipment={equipment.hot_holders || []} logs={logs("hot_holding")} onRecorded={onRecorded} onSaved={onSaved} />}
                {section === "deep_cleaning" && <DeepCleaningForm day={today?.deep_cleaning} onSaved={onSaved} />}
                {section === "incoming" && <IncomingForm onSaved={onSaved} />}
                {section === "cooling" && <CoolingForm onSaved={onSaved} />}
                {section === "surface" && <SurfaceForm onSaved={onSaved} />}
            </DialogContent>
        </Dialog>
    );
}

function DeepCleaningForm({ day, onSaved }) {
    const { t } = useTranslation();
    const { user } = useApp();
    const { scopedParams: params } = useRestaurantScope();
    const selectedDate = useSelectedDate();
    // No default: an existing answer for this date is shown, otherwise nothing is pre-selected.
    const [answer, setAnswer] = useState(day?.answer || null);
    const [notes, setNotes] = useState(day?.log?.notes || "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.post("/daily-checks/deep-cleaning", { answer, notes, check_date: selectedDate }, { params });
            toast.success(t("dailyChecks.deepRecorded", "Deep cleaning recorded"));
            onSaved();
        } catch (e) {
            toast.error(apiErrorMessage(e) || t("dailyChecks.saveFailed", "Could not save — nothing was recorded. Try again."));
        } finally { setSaving(false); }
    };
    const label = "text-xs uppercase tracking-wide font-semibold text-neutral-600";
    return (
        <>
            <DialogHeader>
                <DialogTitle>{t("dailyChecks.recordTitle.deep_cleaning", "Record deep cleaning")}</DialogTitle>
                <DialogDescription>{t("dailyChecks.controlDateLabel", "Control date:")} <strong>{selectedDate}</strong></DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
                <div>
                    <div className={label}>{t("dailyChecks.deepQuestion", "Was deep cleaning carried out?")}</div>
                    <div className="mt-1 flex gap-2" role="radiogroup" data-testid="deep-answer">
                        {[["yes", t("dailyChecks.answerYes", "Yes")], ["no", t("dailyChecks.answerNo", "No")]].map(([v, l]) => (
                            <button key={v} type="button" role="radio" aria-checked={answer === v} data-testid={`deep-answer-${v}`}
                                onClick={() => setAnswer(v)}
                                className={`flex-1 h-11 rounded-md border text-sm font-semibold transition-colors ${answer === v ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-300 hover:bg-neutral-50"}`}>{l}</button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className={label}>{t("dailyChecks.deepNotes", "Notes — what was cleaned or done")}</label>
                    <textarea data-testid="deep-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm" />
                </div>
                <div className="text-xs text-neutral-600" data-testid="deep-responsible">
                    {t("dailyChecks.responsible", "Responsible")}: <strong>{user?.name}</strong>
                </div>
            </div>
            <DialogFooter>
                <button data-testid="save-deep-cleaning" onClick={save} disabled={saving || !answer}
                    className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                    {saving ? t("common.saving", "Saving…") : t("common.save", "Save")}
                </button>
            </DialogFooter>
        </>
    );
}

// Launch Fix R6 — Omavalvonta accepted ranges.
// Kept in sync with backend TEMP_RANGES. Used to (a) label the form with
// the target range and (b) short-circuit the corrective-action prompt if
// the API somehow forgot to return `out_of_range`.
const TEMP_LIMITS = {
    fridge:      { max:  6,  label: "≤ 6 °C",  defaultValue:  4 },
    freezer:     { max: -18, label: "≤ -18 °C", defaultValue: -18 },
    hot_holding: { min: 60,  label: "≥ 60 °C", defaultValue: 65 },
};
const TYPE_TITLE = {
    fridge: "fridge", freezer: "freezer", hot_holding: "hot-holding",
};

function TemperatureForm({ type, equipment, logs = [], onRecorded, onSaved }) {
    const { user, restaurantId } = useApp();
    const { scopedParams: params } = useRestaurantScope();
    const selectedDate = useSelectedDate();
    const limit = TEMP_LIMITS[type] || {};
    const { t } = useTranslation();
    // Equipment already logged for the selected date (server) + saved in this dialog.
    const [savedIds, setSavedIds] = useState(() => new Set(equipment
        .filter((e) => logs.some((l) => l.equipment_id === e.id
            || (l.item_name || "").toLowerCase() === e.name.toLowerCase()))
        .map((e) => e.id)));
    const pending = equipment.filter((e) => !savedIds.has(e.id));
    const [equipmentId, setEquipmentId] = useState(pending[0]?.id || "");
    const [temperature, setTemperature] = useState(limit.defaultValue ?? 4);
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [correctiveFor, setCorrectiveFor] = useState(null);
    const current = equipment.find((e) => e.id === equipmentId);
    const position = equipment.length - pending.length + (current && !savedIds.has(current.id) ? 1 : 0);

    // Move to the next pending equipment after a successful save; close when none remain.
    const advance = (doneId) => {
        const nextSaved = new Set(savedIds).add(doneId);
        setSavedIds(nextSaved);
        if (onRecorded) onRecorded();
        const next = equipment.find((e) => !nextSaved.has(e.id));
        if (!next) { onSaved(); return; }
        setEquipmentId(next.id);
        setTemperature(limit.defaultValue ?? 4);
        setNotes("");
    };

    const save = async () => {
        if (!equipmentId) { toast.error(t("dailyChecks.pickEquipment", "Pick equipment")); return; }
        const item = equipment.find((e) => e.id === equipmentId);
        setSaving(true);
        try {
            const { data } = await api.post("/daily-checks/temperature", {
                log_type: type, item_name: item.name, location: item.location,
                temperature: Number(temperature), notes, equipment_id: item.id,
                check_date: selectedDate,
            }, { params });
            toast.success(t("dailyChecks.temperatureRecorded", "Temperature recorded"));
            // Launch Fix R6 §3 — if the reading is out of range, prompt the
            // chef inline for a corrective action (Omavalvonta requirement).
            if (data?.out_of_range) {
                setCorrectiveFor({ id: data.id, temperature: Number(temperature),
                                     item, type });
                return; // keep the dialog open until the chef decides
            }
            advance(item.id);
        } catch (e) {
            toast.error(apiErrorMessage(e) || t("dailyChecks.saveFailed", "Could not save — nothing was recorded. Try again."));
        } finally { setSaving(false); }
    };

    return (
        <>
            <DialogHeader>
                <DialogTitle>{t(`dailyChecks.recordTitle.${type}`, `Record ${TYPE_TITLE[type]} temperature`)}</DialogTitle>
                <DialogDescription>
                    {t("dailyChecks.pickEquipmentHint", "Pick the equipment, enter °C, save. Target range:")} <strong>{limit.label}</strong>.
                </DialogDescription>
            </DialogHeader>
            {equipment.length === 0 ? (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-md">
                    {t(`dailyChecks.noEquipment.${type}`, `No ${TYPE_TITLE[type]}s configured yet. Add one via Equipment.`)}
                </div>
            ) : (
                <div className="space-y-3">
                    {current && (
                        <div data-testid={`${type}-progress`}
                            className="flex items-center justify-between rounded-md bg-neutral-50 border border-neutral-200 px-3 py-2">
                            <span className="font-display text-lg font-bold" data-testid={`${type}-current-name`}>{current.name}</span>
                            <span className="text-sm font-semibold text-neutral-600">
                                {t("dailyChecks.progressOf", { defaultValue: "{{n}} of {{total}}", n: Math.max(1, position), total: equipment.length })}
                            </span>
                        </div>
                    )}
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.equipmentLabel", "Equipment")}</label>
                        <Select value={equipmentId} onValueChange={setEquipmentId}>
                            <SelectTrigger data-testid={`${type}-equipment`} className="mt-1 h-11"><SelectValue placeholder={t("dailyChecks.choose", "Choose…")} /></SelectTrigger>
                            <SelectContent>
                                {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} <span className="text-neutral-400">· {e.location}</span></SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.temperatureC", "Temperature °C")}</label>
                        <input data-testid={`${type}-temp`} type="number" step="0.1"
                            value={temperature} onChange={(e) => setTemperature(e.target.value)}
                            className="mt-1 w-full h-12 px-3 border border-neutral-300 rounded-md text-xl font-display font-bold" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("common.notes", "Notes")}</label>
                        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm" />
                    </div>
                </div>
            )}
            <DialogFooter>
                <button data-testid={`save-${type}-temp`} onClick={save} disabled={saving || !equipmentId}
                    className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                    {saving ? t("common.saving", "Saving…") : t("common.save", "Save")}
                </button>
            </DialogFooter>
            {correctiveFor && (
                <CorrectiveActionForm
                    log={correctiveFor}
                    onClose={() => { const id = correctiveFor.item.id; setCorrectiveFor(null); advance(id); }}
                />
            )}
        </>
    );
}

// Launch Fix R6 §3 — inline Corrective Action prompt shown right after a
// temperature reading that falls outside the accepted range. Persists on
// the same temperature_logs document — no new module, no new collection.
function CorrectiveActionForm({ log, onClose }) {
    const [action, setAction] = useState("");
    const [notes, setNotes] = useState("");
    const [followUp, setFollowUp] = useState("");
    const [saving, setSaving] = useState(false);
    const { t } = useTranslation();

    const save = async () => {
        if (!action.trim()) { toast.error(t("dailyChecks.describeCorrective", "Describe the corrective action")); return; }
        setSaving(true);
        try {
            await api.post(`/daily-checks/temperature/${log.id}/corrective-action`, {
                corrective_action: action.trim(),
                corrective_notes: notes.trim(),
                follow_up_temperature: followUp === "" ? null : Number(followUp),
            });
            toast.success(t("dailyChecks.correctiveRecorded", "Corrective action recorded"));
            onClose();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally { setSaving(false); }
    };

    return (
        <Dialog open onOpenChange={() => onClose()}>
            <DialogContent data-testid="corrective-action-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-amber-800">
                        <AlertTriangle className="h-5 w-5" /> {t("dailyChecks.outOfRangeTitle", "Out of range — record a corrective action")}
                    </DialogTitle>
                    <DialogDescription>
                        {t("dailyChecks.recordedAt", { defaultValue: "{{name}} was recorded at", name: log.item.name })} <strong>{log.temperature} °C</strong>{t("dailyChecks.describeAfterTemp", ". Describe the action taken so it becomes part of the check history.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">
                            {t("dailyChecks.correctiveAction", "Corrective action *")}
                        </label>
                        <textarea rows={2}
                            data-testid="corrective-action-text"
                            placeholder={t("dailyChecks.correctivePlaceholder", "e.g. Moved products to another fridge. Maintenance informed.")}
                            value={action} onChange={(e) => setAction(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">
                            {t("dailyChecks.optionalNotes", "Optional notes")}
                        </label>
                        <textarea rows={2}
                            data-testid="corrective-action-notes"
                            value={notes} onChange={(e) => setNotes(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">
                            {t("dailyChecks.followUpTemp", "Follow-up temperature °C (optional)")}
                        </label>
                        <input type="number" step="0.1"
                            data-testid="corrective-action-followup"
                            placeholder={t("dailyChecks.followUpPlaceholder", "e.g. 4.0")}
                            value={followUp} onChange={(e) => setFollowUp(e.target.value)}
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md text-lg font-semibold" />
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <button
                        data-testid="corrective-action-skip"
                        onClick={() => onClose()}
                        className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-neutral-50">
                        {t("dailyChecks.skipForNow", "Skip for now")}
                    </button>
                    <button
                        data-testid="corrective-action-save"
                        onClick={save} disabled={saving || !action.trim()}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                        {saving ? t("common.saving", "Saving…") : t("dailyChecks.saveCorrective", "Save corrective action")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Self Monitoring Temperature Correction — compact "Correct reading"
// action embedded in the existing temperature History row. Reuses the
// `/daily-checks/temperature/{id}/correct` endpoint which writes an
// immutable `daily_check_corrections` audit row, preserves the
// original value/timestamp/author and re-evaluates `out_of_range`.
// Correction never deletes, never duplicates and never changes the
// equipment or measurement date.
function CorrectReadingDialog({ log, onClose, onSaved }) {
    const [temperature, setTemperature] = useState(() => String(log?.temperature ?? ""));
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const { t } = useTranslation();
    if (!log) return null;
    const typeLabel = TYPE_TITLE[log.log_type] || "temperature";
    const limit = TEMP_LIMITS[log.log_type] || {};
    const save = async () => {
        if (temperature === "" || Number.isNaN(Number(temperature))) {
            toast.error(t("dailyChecks.enterNumeric", "Enter a numeric temperature")); return;
        }
        if (!reason.trim()) {
            toast.error(t("dailyChecks.reasonRequired", "Reason is required for a correction")); return;
        }
        if (Number(temperature) === Number(log.temperature)) {
            toast.error(t("dailyChecks.enterDifferent", "Enter a different value than the original reading"));
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post(
                `/daily-checks/temperature/${log.id}/correct`,
                { temperature: Number(temperature), reason: reason.trim() });
            toast.success(
                data?.out_of_range
                    ? t("dailyChecks.correctedStillOut", "Reading corrected — still out of range, record a corrective action")
                    : t("dailyChecks.corrected", "Reading corrected"));
            onSaved && onSaved({ ...log, temperature: Number(temperature),
                                   out_of_range: data?.out_of_range });
            onClose();
        } catch (e) {
            toast.error(apiErrorMessage(e)
                || t("dailyChecks.couldNotCorrect", "Could not correct the reading"));
        } finally { setSaving(false); }
    };
    return (
        <Dialog open onOpenChange={() => onClose()}>
            <DialogContent data-testid="correct-reading-dialog">
                <DialogHeader>
                    <DialogTitle>{t(`dailyChecks.correctTitle.${TYPE_TITLE[log.log_type] ? log.log_type : "temperature"}`, `Correct ${typeLabel} reading`)}</DialogTitle>
                    <DialogDescription>
                        {t("dailyChecks.correctFor", "Correct a mistyped value for")} <strong>{log.item_name}</strong> {t("dailyChecks.correctOn", "on")} <strong>{log.check_date || (log.recorded_at || "").slice(0,10)}</strong>{t("dailyChecks.correctPreserved", ". The original reading, time and author are preserved in the correction history.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="text-sm text-neutral-600"
                         data-testid="correct-reading-original">
                        {t("dailyChecks.originalReading", "Original reading:")} <span className="font-mono font-semibold">{log.temperature}°C</span>
                        {log.completed_by ? <> · {t("dailyChecks.byName", { defaultValue: "by {{name}}", name: log.completed_by })}</> : null}
                        {log.recorded_at ? <> · {formatDateTime(log.recorded_at)}</> : null}
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.correctedTemp", "Corrected temperature °C")}</label>
                        <input type="number" step="0.1"
                            data-testid="correct-reading-temp"
                            value={temperature}
                            onChange={(e) => setTemperature(e.target.value)}
                            className="mt-1 w-full h-12 px-3 border border-neutral-300 rounded-md text-xl font-display font-bold" />
                        {limit.label && (
                            <div className="text-[11px] text-neutral-500 mt-1">{t("dailyChecks.targetRange", "Target range:")} {limit.label}</div>
                        )}
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.reasonStar", "Reason *")}</label>
                        <textarea rows={2}
                            data-testid="correct-reading-reason"
                            placeholder={t("dailyChecks.reasonPlaceholder", "e.g. typing mistake — sign dropped")}
                            value={reason} onChange={(e) => setReason(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm" />
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <button
                        data-testid="correct-reading-cancel"
                        onClick={() => onClose()}
                        className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-neutral-50">
                        {t("common.cancel", "Cancel")}
                    </button>
                    <button
                        data-testid="correct-reading-save"
                        onClick={save} disabled={saving || !reason.trim()}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                        {saving ? t("common.saving", "Saving…") : t("dailyChecks.saveCorrection", "Save correction")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function IncomingForm({ onSaved }) {
    const { user, restaurantId } = useApp();
    const { scopedParams: params } = useRestaurantScope();
    const selectedDate = useSelectedDate();
    const [deliveries, setDeliveries] = useState([]);
    const [deliveryId, setDeliveryId] = useState("");
    const [product, setProduct] = useState("");
    const [temperature, setTemperature] = useState(4);
    const [accepted, setAccepted] = useState(true);
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        api.get("/deliveries", { params })
            .then(({ data }) => setDeliveries(data.slice(0, 30)))
            .catch(() => {});
    }, [params]);

    const save = async () => {
        if (!product.trim()) { toast.error(t("dailyChecks.productRequired", "Product required")); return; }
        setSaving(true);
        try {
            const body = {
                product_name: product, temperature: Number(temperature),
                accepted, notes, check_date: selectedDate,
            };
            if (deliveryId) {
                const d = deliveries.find((x) => x.id === deliveryId);
                body.delivery_id = deliveryId;
                if (d) body.supplier_id = d.supplier_id;
            }
            await api.post("/daily-checks/incoming-temperature", body, { params });
            toast.success(t("dailyChecks.incomingRecorded", "Incoming temperature recorded"));
            onSaved();
        } finally { setSaving(false); }
    };

    return (
        <>
            <DialogHeader>
                <DialogTitle>{t("dailyChecks.incomingTitle", "Incoming product temperature")}</DialogTitle>
                <DialogDescription>{t("dailyChecks.incomingDescription", "Optionally link to a recent delivery.")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
                <div>
                    <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.deliveryOptional", "Delivery (optional)")}</label>
                    <Select value={deliveryId || "_none"} onValueChange={(v) => setDeliveryId(v === "_none" ? "" : v)}>
                        <SelectTrigger data-testid="incoming-delivery" className="mt-1 h-11"><SelectValue placeholder={t("dailyChecks.noLink", "No link")} /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="_none">{t("dailyChecks.noLink", "No link")}</SelectItem>
                            {deliveries.map((d) => (
                                <SelectItem key={d.id} value={d.id}>
                                    {d.supplier_name} · {formatDateShort(d.delivery_date)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.product", "Product")}</label>
                    <input data-testid="incoming-product" value={product}
                        onChange={(e) => setProduct(e.target.value)}
                        className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.temperatureC", "Temperature °C")}</label>
                        <input data-testid="incoming-temp" type="number" step="0.1"
                            value={temperature} onChange={(e) => setTemperature(e.target.value)}
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.acceptedQ", "Accepted?")}</label>
                        <Select value={accepted ? "yes" : "no"} onValueChange={(v) => setAccepted(v === "yes")}>
                            <SelectTrigger data-testid="incoming-accepted" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="yes">{t("dailyChecks.yes", "Yes")}</SelectItem>
                                <SelectItem value="no">{t("dailyChecks.no", "No")}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div>
                    <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("common.notes", "Notes")}</label>
                    <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm" />
                </div>
            </div>
            <DialogFooter>
                <button data-testid="save-incoming" onClick={save} disabled={saving}
                    className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                    {saving ? t("common.saving", "Saving…") : t("common.save", "Save")}
                </button>
            </DialogFooter>
        </>
    );
}

function CoolingForm({ onSaved }) {
    const { user, restaurantId } = useApp();
    const { scopedParams: params } = useRestaurantScope();
    const selectedDate = useSelectedDate();
    // Phase 1 (2026-02, Message 351) — time-only UI. The Daily Checks
    // top-level selectedDate is the authoritative operational date;
    // this form only asks for HH:MM. The full timestamp is composed
    // server-side from selectedDate + HH:MM.
    const todayIso = new Date().toISOString().slice(0, 10);
    const nowHHMM = new Date().toTimeString().slice(0, 5);
    // When entering a historical date, do NOT fabricate today's time
    // — leave blank so the chef enters the actual historical time.
    const isHistorical = selectedDate !== todayIso;
    const [product, setProduct] = useState("");
    const [startHHMM, setStartHHMM] = useState(isHistorical ? "" : nowHHMM);
    const [endHHMM, setEndHHMM] = useState("");
    const [nextDay, setNextDay] = useState(false);
    const [initial, setInitial] = useState(63);
    const [final, setFinal] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const { t } = useTranslation();

    const save = async () => {
        if (!product.trim()) { toast.error(t("dailyChecks.productRequired", "Product required")); return; }
        if (!startHHMM) { toast.error(t("dailyChecks.startTimeRequired", "Start time required")); return; }
        setSaving(true);
        try {
            await api.post("/daily-checks/cooling-log", {
                product,
                check_date: selectedDate,
                start_hhmm: startHHMM,
                end_hhmm: endHHMM || null,
                next_day: !!nextDay,
                initial_temperature: Number(initial),
                final_temperature: final === "" ? null : Number(final),
                notes,
            }, { params });
            toast.success(t("dailyChecks.coolingSaved", "Cooling log saved"));
            onSaved();
        } catch (err) {
            toast.error(apiErrorMessage(err)
                          || t("dailyChecks.saveFailed", "Save failed"));
        } finally { setSaving(false); }
    };

    return (
        <>
            <DialogHeader>
                <DialogTitle>{t("dailyChecks.coolingTitle", "Cooling log")}</DialogTitle>
                <DialogDescription>{t("dailyChecks.coolingDescription", "Track product cooling. You can finish later.")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
                <div className="text-xs text-neutral-500" data-testid="cooling-date-readonly">
                    {t("dailyChecks.dateLabel", "Date:")} <span className="font-semibold text-neutral-800">{selectedDate}</span>
                </div>
                <div>
                    <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.product", "Product")}</label>
                    <input data-testid="cooling-product" value={product}
                        onChange={(e) => setProduct(e.target.value)}
                        className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.startTime", "Start time (HH:MM)")}</label>
                        <input data-testid="cooling-start" type="time"
                            value={startHHMM} onChange={(e) => setStartHHMM(e.target.value)}
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.endTimeOptional", "End time (optional)")}</label>
                        <input data-testid="cooling-end" type="time"
                            value={endHHMM} onChange={(e) => setEndHHMM(e.target.value)}
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.initialC", "Initial °C")}</label>
                        <input data-testid="cooling-initial" type="number" step="0.1"
                            value={initial} onChange={(e) => setInitial(e.target.value)}
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.finalCOptional", "Final °C (optional)")}</label>
                        <input data-testid="cooling-final" type="number" step="0.1"
                            value={final} onChange={(e) => setFinal(e.target.value)}
                            className="mt-1 w-full h-11 px-3 border border-neutral-300 rounded-md" />
                    </div>
                </div>
                {endHHMM && startHHMM && endHHMM < startHHMM && (
                    <label className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2"
                             data-testid="cooling-next-day-toggle">
                        <input type="checkbox" checked={nextDay}
                            onChange={(e) => setNextDay(e.target.checked)} />
                        {t("dailyChecks.nextDayHint", "End time is earlier than start — cooling finished on the next day.")}
                    </label>
                )}
                <div>
                    <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("common.notes", "Notes")}</label>
                    <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm" />
                </div>
            </div>
            <DialogFooter>
                <button data-testid="save-cooling" onClick={save} disabled={saving}
                    className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                    {saving ? t("common.saving", "Saving…") : t("common.save", "Save")}
                </button>
            </DialogFooter>
        </>
    );
}

function SurfaceForm({ onSaved }) {
    const { user, restaurantId } = useApp();
    const { scopedParams: params } = useRestaurantScope();
    const selectedDate = useSelectedDate();
    const fileRef = useRef(null);
    // Phase 1 (2026-02, Message 351) — multi-sample session model.
    // Selected Daily Checks date is authoritative; no separate date
    // picker. Overall pass/fail is computed server-side.
    const emptySample = () => ({ id: Math.random().toString(36).slice(2),
                                    location: "", result: "pass", notes: "" });
    const [samples, setSamples] = useState([emptySample()]);
    const [laboratory, setLaboratory] = useState("");
    const [notes, setNotes] = useState("");
    const [nextDue, setNextDue] = useState("");
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const { t } = useTranslation();

    const updateSample = (idx, patch) => setSamples(s => {
        const copy = [...s]; copy[idx] = { ...copy[idx], ...patch }; return copy;
    });
    const addSample = () => setSamples(s => [...s, emptySample()]);
    const removeSample = (idx) => setSamples(s => s.length > 1
        ? s.filter((_, i) => i !== idx) : s);

    const save = async () => {
        if (!samples.length || samples.some(s => !s.location.trim())) {
            toast.error(t("dailyChecks.sampleNeedsLocation", "Each sample needs a location"));
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/surface-tests", {
                test_date: selectedDate,
                samples: samples.map(s => ({
                    location: s.location.trim(),
                    result: s.result,
                    notes: s.notes,
                })),
                laboratory, notes,
                ...(nextDue ? { next_due_date: nextDue } : {}),
            }, { params });
            if (file) {
                const fd = new FormData();
                fd.append("file", file);
                await api.post(`/surface-tests/${data.id}/documents`, fd,
                    { headers: { "Content-Type": "multipart/form-data" } });
            }
            toast.success(t("dailyChecks.surfaceRecorded", { defaultValue: "Surface test recorded ({{result}})", result: t(`dailyChecks.result.${data.overall_result || "pass"}`, data.overall_result || "pass").toUpperCase() }));
            onSaved();
        } catch (err) {
            toast.error(apiErrorMessage(err) || t("dailyChecks.saveFailed", "Save failed"));
        } finally { setSaving(false); }
    };

    return (
        <>
            <DialogHeader>
                <DialogTitle>{t("dailyChecks.surfaceTitle", "Surface hygiene test")}</DialogTitle>
                <DialogDescription>{t("dailyChecks.surfaceDescription", "Record each sampled surface. Attach the laboratory report if you have one.")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
                <div className="text-xs text-neutral-500" data-testid="surface-date-readonly">
                    {t("dailyChecks.dateLabel", "Date:")} <span className="font-semibold text-neutral-800">{selectedDate}</span>
                </div>
                <div className="space-y-3" data-testid="surface-samples">
                    {samples.map((s, idx) => (
                        <div key={s.id} className="border border-neutral-200 rounded-md p-3 space-y-2"
                                data-testid={`surface-sample-${idx}`}>
                            <div className="flex items-center justify-between">
                                <span className="text-xs uppercase tracking-wide font-semibold text-neutral-600">
                                    {t("dailyChecks.sampleN", { defaultValue: "Sample {{n}}", n: idx + 1 })}
                                </span>
                                {samples.length > 1 && (
                                    <button onClick={() => removeSample(idx)}
                                        data-testid={`remove-sample-${idx}`}
                                        className="text-xs text-red-600 hover:underline">
                                        {t("dailyChecks.remove", "Remove")}
                                    </button>
                                )}
                            </div>
                            <input data-testid={`sample-location-${idx}`}
                                placeholder={t("dailyChecks.locationPlaceholder", "Surface / Location (e.g. Cutting board)")}
                                value={s.location}
                                onChange={(e) => updateSample(idx, { location: e.target.value })}
                                className="w-full h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                            <div className="flex items-center gap-3">
                                <Select value={s.result}
                                        onValueChange={(v) => updateSample(idx, { result: v })}>
                                    <SelectTrigger data-testid={`sample-result-${idx}`}
                                        className="h-10 w-32"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="pass">{t("dailyChecks.pass", "Pass")}</SelectItem>
                                        <SelectItem value="fail">{t("dailyChecks.fail", "Fail")}</SelectItem>
                                    </SelectContent>
                                </Select>
                                <input data-testid={`sample-notes-${idx}`}
                                    placeholder={t("dailyChecks.sampleNotesPlaceholder", "Sample notes (optional)")}
                                    value={s.notes}
                                    onChange={(e) => updateSample(idx, { notes: e.target.value })}
                                    className="flex-1 h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                            </div>
                        </div>
                    ))}
                    <button onClick={addSample} data-testid="add-sample"
                        className="w-full h-10 rounded-md border border-dashed border-neutral-300 text-sm font-semibold text-neutral-700 hover:bg-neutral-50">
                        {t("dailyChecks.addSample", "+ Add sample")}
                    </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.laboratoryOptional", "Laboratory (optional)")}</label>
                        <input data-testid="surface-lab" value={laboratory}
                            onChange={(e) => setLaboratory(e.target.value)}
                            className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.nextDueOptional", "Next due (optional)")}</label>
                        <input type="date" data-testid="surface-next" value={nextDue}
                            onChange={(e) => setNextDue(e.target.value)}
                            className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                    </div>
                </div>
                <div>
                    <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.sessionNotes", "Session notes")}</label>
                    <textarea rows={2} value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="mt-1 w-full px-3 py-2 border border-neutral-300 rounded-md text-sm" />
                </div>
                <div className="flex items-center gap-2">
                    <input type="file" ref={fileRef} className="hidden" data-testid="surface-file-input"
                        accept="image/*,application/pdf"
                        onChange={(e) => setFile(e.target.files?.[0] || null)} />
                    <button onClick={() => fileRef.current?.click()}
                        data-testid="surface-attach"
                        className="h-9 px-3 inline-flex items-center gap-2 rounded-md border border-neutral-300 text-xs font-semibold hover:bg-neutral-50">
                        <Upload className="h-3.5 w-3.5" /> {t("dailyChecks.attachReport", "Attach report")}
                    </button>
                    {file && <span className="text-xs text-neutral-600"><FileText className="inline h-3.5 w-3.5 mr-1" />{file.name}</span>}
                </div>
            </div>
            <DialogFooter>
                <button data-testid="save-surface" onClick={save} disabled={saving}
                    className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                    {saving ? t("common.saving", "Saving…") : t("common.save", "Save")}
                </button>
            </DialogFooter>
        </>
    );
}

function HistoryDialog({ section, onClose }) {
    const { user, restaurantId } = useApp();
    const { scopedParams: params } = useRestaurantScope();
    const [items, setItems] = useState([]);
    const [correcting, setCorrecting] = useState(null);
    const { t } = useTranslation();

    const refresh = useCallback(() => {
        if (!section) return;
        api.get("/daily-checks/history", { params: { ...params, section } })
            .then(({ data }) => setItems(data));
    }, [section, params]);
    useEffect(() => {
        refresh();
    }, [refresh]);

    if (!section) return null;
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-3xl" data-testid="history-dialog">
                <DialogHeader>
                    <DialogTitle>{t(`dailyChecks.historyTitle.${section}`, `${section.replace("_", " ")} history`)}</DialogTitle>
                </DialogHeader>
                {items.length === 0 ? (
                    <div className="text-sm text-neutral-500 italic">{t("dailyChecks.noHistory", "No history yet.")}</div>
                ) : (
                    <div className="kop-card overflow-x-auto max-h-[60vh] overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-neutral-50 sticky top-0">
                                <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                                    {section === "fridge" || section === "freezer" || section === "hot_holding" ? (
                                        <>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.date", "Date")}</th>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.equipment", "Equipment")}</th>
                                            <th className="px-4 py-2 font-semibold">°C</th>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.by", "By")}</th>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.corrective", "Corrective action")}</th>
                                        </>
                                    ) : section === "incoming" ? (
                                        <>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.date", "Date")}</th>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.product", "Product")}</th>
                                            <th className="px-4 py-2 font-semibold">°C</th>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.accepted", "Accepted")}</th>
                                        </>
                                    ) : section === "deep_cleaning" ? (
                                        <>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.controlDate", "Control date")}</th>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.task", "Task")}</th>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.result", "Result")}</th>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.by", "By")}</th>
                                        </>
                                    ) : section === "cooling" ? (
                                        <>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.start", "Start")}</th>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.end", "End")}</th>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.product", "Product")}</th>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.initFinal", "Init→Final °C")}</th>
                                        </>
                                    ) : (
                                        <>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.testDate", "Test date")}</th>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.nextDue", "Next due")}</th>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.location", "Location")}</th>
                                            <th className="px-4 py-2 font-semibold">{t("dailyChecks.col.result", "Result")}</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-100">
                                {items.map((it) => (
                                    <tr key={it.id}>
                                        {section === "fridge" || section === "freezer" || section === "hot_holding" ? (
                                            <>
                                                <td className="px-4 py-2 text-neutral-600">{formatDateTime(it.recorded_at)}</td>
                                                <td className="px-4 py-2 font-medium">{it.item_name}</td>
                                                <td className={`px-4 py-2 font-mono ${it.out_of_range ? "text-amber-800 font-bold" : ""}`}>
                                                    {it.temperature}°{it.out_of_range && " ⚠"}
                                                </td>
                                                <td className="px-4 py-2 text-neutral-500">{it.completed_by || "—"}</td>
                                                <td className="px-4 py-2 text-neutral-600 max-w-xs">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="min-w-0">
                                                            {it.corrective_action ? (
                                                                <div data-testid={`corrective-record-${it.id}`}>
                                                                    <div>{it.corrective_action}</div>
                                                                    {it.follow_up_temperature != null && (
                                                                        <div className="text-xs text-neutral-500 mt-0.5">
                                                                            {t("dailyChecks.followUp", "Follow-up:")} <span className="font-mono">{it.follow_up_temperature}°</span>
                                                                        </div>
                                                                    )}
                                                                    {it.corrective_notes && (
                                                                        <div className="text-xs text-neutral-500 italic mt-0.5">
                                                                            {it.corrective_notes}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span className="text-neutral-300">—</span>
                                                            )}
                                                        </div>
                                                        <button
                                                            data-testid={`correct-reading-${it.id}`}
                                                            onClick={() => setCorrecting(it)}
                                                            title={t("dailyChecks.correctReadingTitle", "Correct a mistyped reading")}
                                                            className="shrink-0 h-7 px-2 inline-flex items-center text-[11px] font-semibold text-neutral-700 border border-neutral-200 rounded-md hover:bg-neutral-50">
                                                            {t("dailyChecks.correctReading", "Correct reading")}
                                                        </button>
                                                    </div>
                                                </td>
                                            </>
                                        ) : section === "incoming" ? (
                                            <>
                                                <td className="px-4 py-2 text-neutral-600">{formatDateTime(it.recorded_at)}</td>
                                                <td className="px-4 py-2 font-medium">{it.product_name}</td>
                                                <td className="px-4 py-2 font-mono">{it.temperature}°</td>
                                                <td className="px-4 py-2">
                                                    <StatusBadge status={it.accepted ? "completed" : "action_required"}>
                                                        {it.accepted ? t("dailyChecks.yes", "Yes") : t("dailyChecks.no", "No")}
                                                    </StatusBadge>
                                                </td>
                                            </>
                                        ) : section === "deep_cleaning" ? (
                                            <>
                                                <td className="px-4 py-2 text-neutral-600">{it.check_date}</td>
                                                <td className="px-4 py-2 font-medium">{it.task_name ? `${it.task_name}${it.area ? ` · ${it.area}` : ""}` : t("dailyChecks.deepDaily", "Daily deep cleaning")}{it.notes ? <div className="text-xs text-neutral-500">{it.notes}</div> : null}</td>
                                                <td className="px-4 py-2 font-semibold">
                                                    {it.result === "done" ? t("dailyChecks.answerYes", "Yes") : t("dailyChecks.answerNo", "No")}
                                                </td>
                                                <td className="px-4 py-2 text-neutral-600">{it.completed_by}</td>
                                            </>
                                        ) : section === "cooling" ? (
                                            <>
                                                <td className="px-4 py-2 text-neutral-600">{formatDateTime(it.start_time)}</td>
                                                <td className="px-4 py-2 text-neutral-600">{it.end_time ? formatDateTime(it.end_time) : "—"}</td>
                                                <td className="px-4 py-2 font-medium">{it.product}</td>
                                                <td className="px-4 py-2 font-mono">{it.initial_temperature}° → {it.final_temperature ?? "—"}°</td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="px-4 py-2 text-neutral-600">{it.test_date}</td>
                                                <td className="px-4 py-2 text-neutral-600">{it.next_due_date}</td>
                                                <td className="px-4 py-2 font-medium">{it.location}</td>
                                                <td className="px-4 py-2">
                                                    <StatusBadge status={it.result === "pass" ? "completed" : "action_required"}>
                                                        {t(`dailyChecks.result.${it.result}`, it.result)}
                                                    </StatusBadge>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {correcting && (
                    <CorrectReadingDialog
                        log={correcting}
                        onClose={() => setCorrecting(null)}
                        onSaved={() => refresh()}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}

function EquipmentDialog({ open, onClose, onSaved, equipment }) {
    const { user, restaurantId } = useApp();
    const { scopedParams: params } = useRestaurantScope();
    const [name, setName] = useState("");
    const [equipmentType, setEquipmentType] = useState("fridge");
    const [location, setLocation] = useState("");
    const [saving, setSaving] = useState(false);
    const { t } = useTranslation();

    const add = async () => {
        if (!name.trim()) { toast.error(t("dailyChecks.nameRequired", "Name required")); return; }
        setSaving(true);
        try {
            await api.post("/equipment", {
                name, equipment_type: equipmentType, location,
            }, { params });
            toast.success(t("dailyChecks.equipmentAdded", "Equipment added"));
            setName(""); setLocation("");
            onSaved();
        } finally { setSaving(false); }
    };

    const archive = async (eid) => {
        await api.delete(`/equipment/${eid}`);
        onSaved();
    };

    const all = [...equipment.fridges.map((e) => ({ ...e, equipment_type: "fridge" })),
                 ...equipment.freezers.map((e) => ({ ...e, equipment_type: "freezer" }))];

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent data-testid="equipment-dialog">
                <DialogHeader>
                    <DialogTitle>{t("dailyChecks.equipment", "Equipment")}</DialogTitle>
                    <DialogDescription>{t("dailyChecks.equipmentDescription", "Refrigerators & freezers in this restaurant.")}</DialogDescription>
                </DialogHeader>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                    {all.length === 0 && <div className="text-sm text-neutral-500 italic">{t("dailyChecks.noneYet", "None yet.")}</div>}
                    {all.map((e) => (
                        <div key={e.id} className="flex items-center justify-between border border-neutral-200 rounded-md px-3 py-2">
                            <div>
                                <div className="font-medium text-sm">{e.name}</div>
                                <div className="text-xs text-neutral-500 capitalize">{t(`dailyChecks.typeLower.${e.equipment_type}`, e.equipment_type)} · {e.location}</div>
                            </div>
                            <button data-testid={`archive-equip-${e.id}`}
                                onClick={() => archive(e.id)}
                                className="h-8 px-2 text-xs font-semibold text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-md">
                                {t("dailyChecks.archive", "Archive")}
                            </button>
                        </div>
                    ))}
                </div>
                <div className="border-t border-neutral-200 pt-4 grid grid-cols-2 gap-2">
                    <div className="col-span-2">
                        <input data-testid="equip-name" placeholder={t("dailyChecks.equipNamePlaceholder", "Name (e.g. Walk-in Fridge)")}
                            value={name} onChange={(e) => setName(e.target.value)}
                            className="w-full h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                    </div>
                    <Select value={equipmentType} onValueChange={setEquipmentType}>
                        <SelectTrigger data-testid="equip-type" className="h-10"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="fridge">{t("dailyChecks.type.fridge", "Fridge")}</SelectItem>
                            <SelectItem value="freezer">{t("dailyChecks.type.freezer", "Freezer")}</SelectItem>
                            <SelectItem value="hot_holding">{t("dailyChecks.type.hot_holding", "Hot Holding")}</SelectItem>
                        </SelectContent>
                    </Select>
                    <input data-testid="equip-location" placeholder={t("dailyChecks.locationPlaceholderShort", "Location")}
                        value={location} onChange={(e) => setLocation(e.target.value)}
                        className="h-10 px-3 border border-neutral-300 rounded-md text-sm" />
                </div>
                <DialogFooter>
                    <button data-testid="add-equip" onClick={add} disabled={saving}
                        className="h-10 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60">
                        {saving ? t("dailyChecks.adding", "Adding…") : t("dailyChecks.addEquipment", "Add equipment")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// Launch Fix R6.1 — Monthly compliance PDF.
// Picks a month, calls GET /api/daily-checks/compliance-pdf and downloads
// the resulting file. Reuses browser fetch so we can attach the auth cookie
// and preserve the server-provided filename.
function ComplianceReportDialog({ open, onClose }) {
    const { user, restaurantId } = useApp();
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [months, setMonths] = useState([now.getMonth() + 1]); // 1..12
    const [busy, setBusy] = useState(false);
    const { t } = useTranslation();

    const MONTHS = ["January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"];
    const YEARS = [];
    for (let y = now.getFullYear() - 3; y <= now.getFullYear(); y++) YEARS.push(y);

    const download = async () => {
        setBusy(true);
        try {
            const qs = new URLSearchParams({ year, months: [...months].sort((a, b) => a - b).join(",") });
            if (user.role === "corporate" && restaurantId) {
                qs.set("restaurant_id", restaurantId);
            }
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/daily-checks/compliance-pdf?${qs}`;
            const res = await fetch(url, { credentials: "include" });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const cd = res.headers.get("Content-Disposition") || "";
            const nameMatch = cd.match(/filename="?([^"]+)"?/);
            const filename = nameMatch ? nameMatch[1]
                : `omavalvontaraportti_${year}_${[...months].sort((a, b) => a - b).map((m) => String(m).padStart(2, "0")).join("-")}.pdf`;
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            toast.success(t("dailyChecks.complianceDownloaded", "Compliance report downloaded"));
            onClose();
        } catch (e) {
            toast.error(t("dailyChecks.complianceFailed", "Could not generate the compliance PDF"));
        } finally { setBusy(false); }
    };

    if (!open) return null;
    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent data-testid="compliance-pdf-dialog">
                <DialogHeader>
                    <DialogTitle>{t("dailyChecks.complianceTitle", "Monthly compliance PDF")}</DialogTitle>
                    <DialogDescription>
                        {t("dailyChecks.complianceDescription", "Generates a printable audit-ready summary of fridge, freezer, hot-holding, incoming, cooling, surface tests and skip-today records for the selected month. Ready for the health inspector.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.year", "Year")}</label>
                        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                            <SelectTrigger data-testid="compliance-year" className="mt-1 h-10">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {YEARS.map((y) => (
                                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <MonthToggleGrid months={months} setMonths={setMonths} labels={MONTHS} />
                </div>
                <DialogFooter className="gap-2">
                    <button
                        onClick={onClose}
                        className="h-10 px-4 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-neutral-50"
                    >
                        {t("common.cancel", "Cancel")}
                    </button>
                    <button
                        data-testid="compliance-download"
                        onClick={download} disabled={busy || months.length === 0}
                        className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 disabled:opacity-60"
                    >
                        <FileText className="h-4 w-4" />
                        {busy ? t("dailyChecks.generating", "Generating…") : t("dailyChecks.downloadPdf", "Download PDF")}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function MonthToggleGrid({ months, setMonths, labels }) {
    const { t } = useTranslation();
    const toggle = (m) => setMonths((cur) => cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m]);
    return (
        <div data-testid="compliance-months">
            <label className="text-xs uppercase tracking-wide font-semibold text-neutral-600">{t("dailyChecks.months", "Months")}</label>
            <div className="mt-1 grid grid-cols-3 sm:grid-cols-4 gap-2">
                {labels.map((m, i) => {
                    const on = months.includes(i + 1);
                    return (
                        <button
                            key={m} type="button" aria-pressed={on}
                            data-testid={`compliance-month-${i + 1}`}
                            onClick={() => toggle(i + 1)}
                            className={`h-9 rounded-md border text-sm font-medium transition-colors ${on
                                ? "bg-neutral-900 text-white border-neutral-900"
                                : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50"}`}
                        >
                            {t(`months.${i + 1}`, m)}
                        </button>
                    );
                })}
            </div>
            {months.length === 0 && (
                <p data-testid="compliance-months-empty" className="mt-2 text-xs text-red-600">
                    {t("dailyChecks.selectAtLeastOneMonth", "Select at least one month")}
                </p>
            )}
        </div>
    );
}
